import { useState } from 'react';
import { commissionsAPI } from '../services/api';
import * as XLSX from 'xlsx';
import { fmt } from '../utils/format';
import Sidebar from '../components/layout/Sidebar';
import toast from 'react-hot-toast';
import { avisoDeFecha, NOTA_NOTAS_DE_CREDITO } from '../utils/comisiones';

/**
 * Comisiones sobre lo COBRADO en el periodo.
 *
 * Misma aritmetica que el Excel que se le manda a Rosamalia: el backend llama
 * a commissionService, que es la unica fuente. Lo que el motor no puede
 * resolver solo no se adivina, se separa en "Para revisar" y lo decide ella.
 */
export default function CommissionsPage() {
  const [desde, setDesde]       = useState('');
  const [hasta, setHasta]       = useState('');
  const [data, setData]         = useState(null);
  const [cargando, setCargando] = useState(false);
  const [detalle, setDetalle]   = useState(false);

  // Aviso de que antes del 22 de agosto los numeros no cuadran con lo que ya
  // se pago. La fecha vive en utils/comisiones para no repetirla aqui.
  const avisoFecha = avisoDeFecha(desde);


  // Exporta exactamente lo que esta en pantalla. A proposito NO vuelve a
  // correr el reporte: cada renglon es una consulta a Odoo y el corte tarda
  // minutos. Las hojas son las mismas que ya conoce Rosamalia.
  const exportar = () => {
    if (!data) return;
    const wb = XLSX.utils.book_new();

    const res = [
      ['COMISIONES DE VENTAS - AMS San Antonio'],
      ['Periodo (cobrado): ' + data.desde + ' al ' + data.hasta],
      ['Generado: ' + new Date(data.generado || Date.now()).toLocaleString('es-MX')],
      ['Base: monto aplicado menos el impuesto de la factura que aun no se haya absorbido en cobros anteriores'],
      ...(avisoDeFecha(data.desde)
        ? [[], ['NOTA SOBRE ESTE PERIODO'], [avisoDeFecha(data.desde)]]
        : []),
      [],
      ['Vendedor', 'Cobrado', 'Base comisionable', 'Comision', 'Override'],
    ];
    for (const v of data.resumen)
      res.push([v.vendedor, v.cobrado, v.base, v.comision, v.override]);
    res.push([]);
    res.push(['TOTAL', data.totales.cobrado, null, data.totales.comision, data.totales.override]);
    res.push([]);
    res.push(['Override Jose Luis (Mango Home)', null, null, null, data.totales.override]);
    res.push([]);
    res.push(['Renglones para revisar: ' + (data.revisar || []).length +
              '    Avisos: ' + (data.avisos || []).length]);
    const hr = XLSX.utils.aoa_to_sheet(res);
    hr['!cols'] = [{wch:36},{wch:16},{wch:18},{wch:14},{wch:14}];
    XLSX.utils.book_append_sheet(wb, hr, 'Resumen');

    // json_to_sheet deriva las columnas de las llaves, asi que no se pierde
    // ningun campo del motor aunque le agreguemos mas campos despues.
    const hoja = (rows) => {
      const h = XLSX.utils.json_to_sheet(rows || []);
      h['!cols'] = Array.from({ length: 28 }, () => ({ wch: 16 }));
      return h;
    };
    XLSX.utils.book_append_sheet(wb, hoja(data.renglones), 'Detalle');
    if ((data.revisar || []).length)
      XLSX.utils.book_append_sheet(wb, hoja(data.revisar), 'Revisar');
    if ((data.avisos || []).length)
      XLSX.utils.book_append_sheet(wb, hoja(data.avisos), 'Avisos');
    const jl = (data.renglones || []).filter(x => Number(x.ovr_monto) !== 0);
    if (jl.length) XLSX.utils.book_append_sheet(wb, hoja(jl), 'Jose Luis');

    XLSX.writeFile(wb, 'Comisiones_' + data.desde + '_a_' + data.hasta + '.xlsx');
    toast.success('Excel generado');
  };

  const generar = async () => {
    if (!desde || !hasta) return toast.error('Faltan las fechas del periodo');
    setCargando(true); setData(null);
    try {
      const r = await commissionsAPI.report(desde, hasta);
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.error || 'No se pudo generar el reporte');
    } finally {
      setCargando(false);
    }
  };

  const C = (n) => fmt.currency(n || 0);
  const PCT = (p) => (p == null ? '-' : (p * 100).toFixed(2).replace(/\.?0+$/, '') + '%');

  const rojo = { color: 'var(--danger, #c0392b)' };

  return (
    <div className="app-shell">
      <Sidebar />
      <div className="main-content">
        <div className="page-header">
          <div>
            <h2>Comisiones</h2>
            <div className="page-subtitle">
              Sobre lo cobrado en el periodo, contra Odoo
            </div>
          </div>
        </div>

        {avisoFecha && (
          <div style={{
            marginBottom: 16, padding: '10px 12px', borderRadius: 6, fontSize: 13,
            lineHeight: 1.6, color: 'var(--text-2)',
            background: 'var(--bg)', border: '1px solid var(--border)',
          }}>
            {avisoFecha}
          </div>
        )}
        <div style={{
          marginBottom: 14, padding: '9px 12px', borderRadius: 6, fontSize: 12.5,
          lineHeight: 1.6, color: 'var(--text-2)',
          background: 'var(--bg)', border: '1px dashed var(--border)',
        }}>
          {NOTA_NOTAS_DE_CREDITO}
        </div>
        <div className="flex items-center gap-2" style={{ marginBottom: 16, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 12, color: 'var(--text-3)' }}>Del</span>
          <input type="date" className="form-input" style={{ width: 160 }}
            value={desde} onChange={e => setDesde(e.target.value)} />
          <span style={{ fontSize: 12, color: 'var(--text-3)' }}>al</span>
          <input type="date" className="form-input" style={{ width: 160 }}
            value={hasta} onChange={e => setHasta(e.target.value)} />
          <button className="btn btn-primary" onClick={generar} disabled={cargando}>
            {cargando ? 'Generando...' : 'Generar reporte'}
          </button>
          <button className="btn btn-ghost" onClick={exportar} disabled={!data || cargando}>
            Exportar a Excel
          </button>
        </div>

        {cargando && (
          <div style={{ fontSize: 13, color: 'var(--text-2)', marginBottom: 16 }}>
            Consultando Odoo cobro por cobro. Una quincena tarda varios minutos; no cierres la pestana.
          </div>
        )}

        {data && (
          <>
            <div className="flex gap-3" style={{ marginBottom: 20, flexWrap: 'wrap' }}>
              {[['Cobrado en el periodo', data.totales.cobrado],
                ['Comision de vendedores', data.totales.comision],
                ['Override Jose Luis', data.totales.override]].map(([lbl, val]) => (
                <div key={lbl} style={{ border: '1px solid var(--border)', borderRadius: 8,
                                        padding: '10px 16px', minWidth: 180 }}>
                  <div style={{ fontSize: 11, color: 'var(--text-3)' }}>{lbl}</div>
                  <div style={{ fontSize: 20, fontWeight: 700 }}>{C(val)}</div>
                </div>
              ))}
            </div>

            <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 8 }}>Por vendedor</h3>
            <table className="data-table" style={{ marginBottom: 24 }}>
              <thead>
                <tr>
                  <th>Vendedor</th><th>Renglones</th><th>Cobrado</th>
                  <th>Base</th><th>Comision</th><th>Override JL</th>
                </tr>
              </thead>
              <tbody>
                {data.resumen.map(v => (
                  <tr key={v.vendedor}>
                    <td style={v.vendedor === 'SIN VENDEDOR' ? rojo : undefined}>{v.vendedor}</td>
                    <td>{v.renglones}</td>
                    <td>{C(v.cobrado)}</td>
                    <td>{C(v.base)}</td>
                    <td style={{ fontWeight: 600 }}>{C(v.comision)}</td>
                    <td>{C(v.override)}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {data.revisar.length > 0 && (
              <>
                <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 4, ...rojo }}>
                  Para revisar ({data.revisar.length})
                </h3>
                <div style={{ fontSize: 12, color: 'var(--text-2)', marginBottom: 8 }}>
                  El motor no pudo resolver estos solo. No se adivinan: los decide Rosamalia.
                </div>
                <table className="data-table" style={{ marginBottom: 24 }}>
                  <thead>
                    <tr>
                      <th>Fecha</th><th>SO</th><th>Cliente</th><th>Vendedor</th>
                      <th>Asignado</th><th>Familia</th><th>Tarifa</th><th>%</th>
                      <th>Base</th><th>Comision</th><th>Por que</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.revisar.map((x, i) => {
                      const motivos = [];
                      if (x.tarifa_code === 'SIN TARIFA') motivos.push('sin tarifa');
                      if (x.vendedor === 'SIN VENDEDOR') motivos.push('sin vendedor');
                      if (x.discrepancia === 'SI') motivos.push('vendedor de Odoo distinto al asignado');
                      if (x.familia === 'SIN CLASIFICAR') motivos.push('familia sin clasificar');
                      if (x.pct_otro_origen != null && x.pct_otro_origen !== x.pct)
                        motivos.push('casa vs propia cambia a ' + PCT(x.pct_otro_origen));
                      if (x.pct_alterno != null && x.pct_alterno !== x.pct)
                        motivos.push('la otra familia pagaria ' + PCT(x.pct_alterno));
                      return (
                        <tr key={i} style={rojo}>
                          <td>{String(x.fecha).slice(0, 10)}</td>
                          <td>{x.so}</td>
                          <td>{x.cliente}</td>
                          <td>{x.vendedor}</td>
                          <td>{x.vend_asignado || '-'}</td>
                          <td>{x.familia}</td>
                          <td>{x.tarifa_code}</td>
                          <td>{PCT(x.pct)}</td>
                          <td>{C(x.base)}</td>
                          <td>{C(x.comision)}</td>
                          <td style={{ fontSize: 11 }}>{motivos.join(' · ')}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </>
            )}

            {data.avisos.length > 0 && (
              <>
                <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 8, ...rojo }}>
                  Avisos ({data.avisos.length})
                </h3>
                <table className="data-table" style={{ marginBottom: 24 }}>
                  <thead>
                    <tr><th>Tipo</th><th>Fecha</th><th>Cliente</th><th>Referencia</th><th>Monto</th></tr>
                  </thead>
                  <tbody>
                    {data.avisos.map((a, i) => (
                      <tr key={i} style={rojo}>
                        <td>{a.tipo}</td>
                        <td>{String(a.fecha || '').slice(0, 10)}</td>
                        <td>{a.cliente}</td>
                        <td>{a.nc || a.pago || '-'}</td>
                        <td>{C(a.monto)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}

            <button className="btn btn-ghost btn-sm" onClick={() => setDetalle(d => !d)}>
              {detalle ? 'Ocultar detalle' : 'Ver detalle (' + data.renglones.length + ' renglones)'}
            </button>

            {detalle && (
              <table className="data-table" style={{ marginTop: 12 }}>
                <thead>
                  <tr>
                    <th>Fecha</th><th>Tipo</th><th>SO</th><th>Factura</th><th>Cliente</th>
                    <th>Familia</th><th>Vendedor</th><th>Tarifa</th><th>%</th>
                    <th>Cobrado</th><th>Base</th><th>Comision</th>
                  </tr>
                </thead>
                <tbody>
                  {data.renglones.map((x, i) => (
                    <tr key={i}>
                      <td>{String(x.fecha).slice(0, 10)}</td>
                      <td>{x.tipo}</td>
                      <td>{x.so}</td>
                      <td>{x.factura}</td>
                      <td>{x.cliente}</td>
                      <td>{x.familia}{x.heredada ? ' (heredada de ' + x.heredada + ')' : ''}</td>
                      <td>{x.vendedor}</td>
                      <td>{x.tarifa_code}</td>
                      <td>{PCT(x.pct)}</td>
                      <td>{C(x.monto)}</td>
                      <td>{C(x.base)}</td>
                      <td>{C(x.comision)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 20 }}>
              Generado {new Date(data.generado).toLocaleString()} · periodo {data.desde} al {data.hasta}
            </div>
          </>
        )}
      </div>
    </div>
  );
}